import { useState, useRef, useEffect, useCallback } from "react";
import { Menu, X, ChevronRight, ChevronDown, CalendarDays, Phone } from "lucide-react";
import PhoneButton from "@/components/shared/PhoneButton";
import { PHONE_NUMBER, PHONE_HREF } from "@/components/shared/PhoneButton";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/lib/categories";
import { COMPANY_MEGA_KEY, COMPANY_MEGA_TAGLINE, companyMegaItems } from "@/lib/company-nav";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const megaCardClassName =
  "group relative block rounded-lg overflow-hidden h-[160px] bg-secondary border border-border hover:border-accent/50 hover:shadow-[0_0_30px_-10px_hsl(var(--accent)/0.2)] transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";

type MegaCardProps = {
  name: string;
  desc: string;
  href: string;
  image: string;
};

function MegaMenuCardLink({ name, desc, href, image }: MegaCardProps) {
  return (
    <Link to={href} className={megaCardClassName}>
      <img
        src={image}
        alt={name}
        className="absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105 brightness-105 group-hover:brightness-115"
      />
      <div className="mega-menu-card-glass rounded-lg" aria-hidden />
      <div className="relative z-10 flex flex-col justify-end h-full p-6">
        <span className="block font-display text-xl font-semibold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]">
          {name}
        </span>
        <span className="block text-sm text-white/85 mt-1 leading-relaxed group-hover:text-white transition-colors duration-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.75)]">
          {desc}
        </span>
        <span className="inline-block mt-2 text-sky-300 text-sm font-display uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
          Learn more →
        </span>
      </div>
    </Link>
  );
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  /** Category label, `company` mega, or closed. */
  const [activeMega, setActiveMega] = useState<string | null>(null);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);
  const hoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setActiveMega(null);
    setMobileOpen(false);
    setMobileAccordion(null);
  }, [location.pathname]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setActiveMega(null);
      setMobileOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActiveMega(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategoryEnter = useCallback((label: string) => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setActiveMega(label), 80);
  }, []);

  const handleCompanyEnter = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setActiveMega(COMPANY_MEGA_KEY), 80);
  }, []);

  const handleTriggerLeave = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    hoverTimeout.current = setTimeout(() => setActiveMega(null), 200);
  }, []);

  const handlePaneEnter = useCallback(() => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
  }, []);

  const handlePaneLeave = useCallback(() => {
    hoverTimeout.current = setTimeout(() => setActiveMega(null), 200);
  }, []);

  const handleCategoryClick = useCallback(
    (slug: string) => {
      setActiveMega(null);
      navigate(`/${slug}`);
    },
    [navigate],
  );

  /** Default Company hub when clicking the trigger (mirrors category → hub navigation). */
  const handleCompanyClick = useCallback(() => {
    setActiveMega(null);
    navigate("/about-us");
  }, [navigate]);

  const activeCat = categories.find((c) => c.label === activeMega);
  const showCategoryMega = Boolean(activeMega && activeCat);
  const showCompanyMega = activeMega === COMPANY_MEGA_KEY;
  const showDesktopMega = showCategoryMega || showCompanyMega;

  return (
    <nav ref={navRef} className="fixed top-0 left-0 right-0 z-50 glass-dark border-b border-primary/20" role="navigation">
      <div className="container mx-auto flex items-center gap-3 lg:gap-4 py-3 px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0">
          <img src="/logo.webp" alt="Shine Bright Auto Solutions" width="56" height="56" className="h-14 w-auto brightness-0 invert" />
        </Link>

        {/* Desktop Category Triggers (A1: text + chevron, no pill chrome) */}
        <div className="hidden lg:flex flex-1 min-w-0 justify-center items-center gap-0.5 xl:gap-1.5">
          {categories.map((cat) => (
            <button
              type="button"
              key={cat.label}
              onMouseEnter={() => handleCategoryEnter(cat.label)}
              onMouseLeave={handleTriggerLeave}
              onClick={() => handleCategoryClick(cat.slug)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleCategoryClick(cat.slug);
                }
              }}
              aria-expanded={activeMega === cat.label}
              aria-haspopup="true"
              className={cn(
                "relative inline-flex items-center gap-1 px-2.5 py-2 font-display text-xs font-semibold uppercase tracking-wider transition-colors duration-200 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                activeMega === cat.label ? "text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <span
                className={cn(
                  "border-b-2 border-transparent pb-0.5 transition-colors",
                  activeMega === cat.label && "border-primary text-foreground",
                )}
              >
                {cat.label} It
              </span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 shrink-0 opacity-70 transition-transform duration-200",
                  activeMega === cat.label && "rotate-180",
                )}
                aria-hidden
              />
            </button>
          ))}
        </div>

        {/* Desktop Right Side (Company mega + compact phone) */}
        <div className="hidden lg:flex items-center shrink-0 gap-2 xl:gap-3">
          <button
            type="button"
            onMouseEnter={handleCompanyEnter}
            onMouseLeave={handleTriggerLeave}
            onClick={handleCompanyClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCompanyClick();
              }
            }}
            aria-expanded={showCompanyMega}
            aria-haspopup="true"
            className={cn(
              "relative inline-flex items-center gap-1 px-2.5 py-2 font-display text-xs font-semibold uppercase tracking-wider transition-colors duration-200 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              showCompanyMega ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span
              className={cn(
                "border-b-2 border-transparent pb-0.5 transition-colors",
                showCompanyMega && "border-primary text-foreground",
              )}
            >
              Company
            </span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 opacity-70 transition-transform duration-200",
                showCompanyMega && "rotate-180",
              )}
              aria-hidden
            />
          </button>

          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={PHONE_HREF}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border/60 text-foreground transition-colors hover:bg-white/10 hover:border-primary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`Call ${PHONE_NUMBER}`}
              >
                <Phone className="h-4 w-4" aria-hidden />
              </a>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="z-[60]">
              <p>{PHONE_NUMBER}</p>
            </TooltipContent>
          </Tooltip>

          <Link
            to="/book"
            className="group cta-hover bg-primary text-primary-foreground px-4 py-2 xl:px-5 text-sm font-display uppercase tracking-wider transition rounded-full shadow-[0_0_12px_hsl(210_100%_55%/0.3)] inline-flex items-center gap-2 shrink-0"
          >
            <CalendarDays className="h-4 w-4 bounce-icon" />
            <span>Book Now</span>
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="lg:hidden text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded p-1"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Desktop Mega Menu Pane (category or company) */}
      <AnimatePresence>
        {showDesktopMega && (
          <motion.div
            key={activeMega ?? "closed"}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            onMouseEnter={handlePaneEnter}
            onMouseLeave={handlePaneLeave}
            className="hidden lg:block absolute left-0 right-0 top-full mega-menu-bg border-t border-border"
          >
            <div className="container mx-auto py-10 px-8">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-1 h-5 bg-primary rounded-full" />
                <p className="text-sm uppercase tracking-[0.2em] text-primary font-display font-medium">
                  {showCompanyMega ? COMPANY_MEGA_TAGLINE : activeCat?.tagline}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {showCompanyMega
                  ? companyMegaItems.map((item) => (
                      <MegaMenuCardLink key={item.href} {...item} />
                    ))
                  : activeCat?.services.map((service) => (
                      <MegaMenuCardLink
                        key={service.name}
                        name={service.name}
                        desc={service.desc}
                        href={service.href}
                        image={service.image}
                      />
                    ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="lg:hidden overflow-hidden glass-dark border-t border-border"
          >
            <div className="flex flex-col gap-1 p-4 max-h-[80vh] overflow-y-auto">
              {categories.map((cat) => (
                <div key={cat.label}>
                  <div className="flex items-center">
                    <Link
                      to={`/${cat.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="flex-1 py-3 px-2 font-display text-base font-semibold uppercase tracking-widest text-foreground"
                    >
                      {cat.label} It
                    </Link>
                    <button
                      type="button"
                      onClick={() =>
                        setMobileAccordion((prev) => (prev === cat.label ? null : cat.label))
                      }
                      aria-expanded={mobileAccordion === cat.label}
                      aria-label={`${mobileAccordion === cat.label ? "Collapse" : "Expand"} ${cat.label} services`}
                      className="p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    >
                      <ChevronDown
                        className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                          mobileAccordion === cat.label ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>
                  <AnimatePresence initial={false}>
                    {mobileAccordion === cat.label && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="overflow-hidden"
                      >
                        <div className="flex items-center gap-2 px-2 pb-2">
                          <span className="w-0.5 h-3 bg-primary rounded-full" />
                          <p className="text-xs text-primary font-display tracking-wide">{cat.tagline}</p>
                        </div>
                        {cat.services.map((service) => (
                          <Link
                            key={service.name}
                            to={service.href}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center justify-between gap-3 px-4 py-3 rounded-md hover:bg-secondary/60 transition-colors"
                          >
                            <div className="min-w-0">
                              <span className="block text-sm font-display font-semibold text-foreground">
                                {service.name}
                              </span>
                              <span className="block text-xs text-muted-foreground mt-0.5">{service.desc}</span>
                            </div>
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              {/* Secondary links */}
              <div className="border-t border-border mt-3 pt-3 flex flex-col gap-2">
                <Link
                  to="/insights"
                  onClick={() => setMobileOpen(false)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2 py-2"
                >
                  Insights
                </Link>
                <Link
                  to="/results"
                  onClick={() => setMobileOpen(false)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2 py-2"
                >
                  Results
                </Link>
                <Link
                  to="/about-us"
                  onClick={() => setMobileOpen(false)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors px-2 py-2"
                >
                  About Us
                </Link>
                <PhoneButton variant="inline" className="px-2 py-2 text-sm" />
                <Link
                  to="/book"
                  onClick={() => setMobileOpen(false)}
                  className="group cta-hover bg-primary text-primary-foreground px-5 py-2.5 text-sm font-display uppercase tracking-wider text-center mt-1 rounded-full flex items-center justify-center gap-2"
                >
                  <CalendarDays className="h-4 w-4 bounce-icon" />
                  <span>Book Now</span>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
